import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuthStore } from '@/stores/authStore'
import { useChangePassword } from '@/hooks/useUsers'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Eye, EyeOff, LogIn, ShieldCheck, ArrowLeft, X, Smartphone } from 'lucide-react'
import api from '@/lib/api'

const loginSchema = z.object({
  email: z.string().email('Email invalido'),
  password: z.string().min(1, 'Senha e obrigatoria'),
})

type LoginForm = z.infer<typeof loginSchema>

function normalizeApiError(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: { detail?: unknown } }, message?: string }
  const detail = ax?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    const msgs = detail.map((d) => typeof d === 'string' ? d : (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : ''))
    const joined = msgs.filter(Boolean).join('; ')
    if (joined) return joined
  }
  if (detail && typeof detail === 'object') {
    try { return JSON.stringify(detail) } catch { /* noop */ }
  }
  return ax?.message || fallback
}

export function Login() {
  const navigate = useNavigate()
  const login = useAuthStore((state) => state.login)
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [theme, setTheme] = useState<'light' | 'dark'>('light')

  // 2FA state
  const [totpStep, setTotpStep] = useState(false)
  const [mustChangePassword, setMustChangePassword] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwdError, setPwdError] = useState('')
  const [totpCode, setTotpCode] = useState(['', '', '', '', '', ''])
  const [tempToken, setTempToken] = useState<string | null>(null)
  const [isVerifying, setIsVerifying] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    const stored = localStorage.getItem('nai-theme')
    if (stored === 'dark' || stored === 'light') {
      setTheme(stored)
      document.documentElement.classList.toggle('dark', stored === 'dark')
    } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setTheme('dark')
      document.documentElement.classList.add('dark')
    }
  }, [])

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark'
    setTheme(newTheme)
    localStorage.setItem('nai-theme', newTheme)
    document.documentElement.classList.toggle('dark', newTheme === 'dark')
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  })

  // Helper: fetch user data and complete login
  const changePwdMutation = useChangePassword()
  
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setPwdError('')
    if (newPassword.length < 8) { setPwdError('Senha deve ter no minimo 8 caracteres'); return }
    if (!/[A-Z]/.test(newPassword)) { setPwdError('Senha deve ter pelo menos uma maiuscula'); return }
    if (!/[a-z]/.test(newPassword)) { setPwdError('Senha deve ter pelo menos uma minuscula'); return }
    if (!/\d/.test(newPassword)) { setPwdError('Senha deve ter pelo menos um numero'); return }
    if (newPassword !== confirmPassword) { setPwdError('Senhas nao conferem'); return }
    try {
      const me = await api.get('/v1/auth/me')
      await changePwdMutation.mutateAsync({ userId: me.data.id, data: { new_password: newPassword } })
      setMustChangePassword(false)
      navigate('/', { replace: true })
    } catch (err: unknown) {
      setPwdError(normalizeApiError(err, 'Erro ao alterar senha'))
    }
  }

  const completeLogin = async (accessToken: string, refreshToken: string, redirectTo?: string) => {
    useAuthStore.getState().setTokens(accessToken, refreshToken)

    const { data: userData } = await api.get('/v1/auth/me', {
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    const user = {
      id: userData.id,
      email: userData.email,
      first_name: userData.first_name,
      last_name: userData.last_name,
      full_name: `${userData.first_name} ${userData.last_name}`,
      role_id: userData.role?.id || '',
      role: userData.role || null,
      team_id: null,
      team: null,
      is_active: userData.is_active,
      attributes: userData.attributes || {},
      phone: userData.phone ?? null,
      avatar_url: userData.avatar_url ?? null,
      totp_enabled: userData.totp_enabled ?? false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    login(user, accessToken, refreshToken)
    navigate(redirectTo || '/', { replace: true })
  }

  const onSubmit = async (formData: LoginForm) => {
    setIsLoading(true)
    setError(null)

    try {
      const params = new URLSearchParams()
      params.append('username', formData.email)
      params.append('password', formData.password)

      const { data } = await api.post('/v1/auth/login', params, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      })

      // Case 1: TOTP required - show code input
      if (data.totp_required && data.temp_token) {
        setTempToken(data.temp_token)
        setTotpStep(true)
        setTotpCode(['', '', '', '', '', ''])
        setTimeout(() => inputRefs.current[0]?.focus(), 100)
        return
      }

      // Case 2: TOTP setup required - login and redirect to security setup
      if (data.totp_setup_required) {
        // Store tokens temporarily so setup-2fa page can use them
        useAuthStore.getState().setTokens(data.access_token, data.refresh_token)
        navigate('/setup-2fa', { replace: true })
        return
      }

      // Case 3: Must change password
      if (data.must_change_password && data.access_token) {
        useAuthStore.getState().setTokens(data.access_token, data.refresh_token)
        setMustChangePassword(true)
        return
      }

      // Case 4: Normal login
      await completeLogin(data.access_token, data.refresh_token)
    } catch (err: unknown) {
      setError(normalizeApiError(err, 'Usuário ou senha inválidos. Verifique suas credenciais.'))
    } finally {
      setIsLoading(false)
    }
  }

  // Handle TOTP code input (6 individual digits)
  const handleTotpInput = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return
    const newCode = [...totpCode]
    newCode[index] = value.slice(-1)
    setTotpCode(newCode)

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when all 6 digits entered
    const fullCode = newCode.join('')
    if (fullCode.length === 6) {
      verifyTotp(fullCode)
    }
  }

  const handleTotpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !totpCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleTotpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pasted.length === 6) {
      const newCode = pasted.split('')
      setTotpCode(newCode)
      inputRefs.current[5]?.focus()
      verifyTotp(pasted)
    }
  }

  const verifyTotp = async (code: string) => {
    if (!tempToken) return
    setIsVerifying(true)
    setError(null)

    try {
      const { data } = await api.post('/v1/auth/verify-totp', {
        temp_token: tempToken,
        code,
      })

      await completeLogin(data.access_token, data.refresh_token)
    } catch (err: unknown) {
      setError(normalizeApiError(err, 'Codigo invalido. Tente novamente.'))
      setTotpCode(['', '', '', '', '', ''])
      setTimeout(() => inputRefs.current[0]?.focus(), 100)
    } finally {
      setIsVerifying(false)
    }
  }

  const backToLogin = () => {
    setTotpStep(false)
    setTempToken(null)
    setTotpCode(['', '', '', '', '', ''])
    setError(null)
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Background Image with Overlay */}
      <div
        className="hidden lg:flex flex-1 relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/bg_portal_cliente_nai.png')" }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to top, rgba(0, 20, 50, 0.9) 0%, rgba(0, 20, 50, 0.5) 40%, rgba(0, 20, 50, 0.15) 100%)'
          }}
        />
        <div className="relative z-10 flex flex-col justify-end p-12 pb-16 text-white w-full">
          <div className="max-w-lg">
            <h1 className="text-4xl xl:text-5xl font-extrabold mb-4 leading-tight drop-shadow-lg">
              Nai Growth
            </h1>
            <p className="text-lg xl:text-xl text-white/90 leading-relaxed drop-shadow-md">
              Gerencie seu pipeline de vendas, acompanhe oportunidades do LinkedIn e potencialize seus resultados comerciais.
            </p>
            <div className="mt-8 flex gap-8">
              <div>
                <div className="text-2xl font-bold">CRM</div>
                <div className="text-white/60 text-sm">Integrado</div>
              </div>
              <div>
                <div className="text-2xl font-bold">LinkedIn</div>
                <div className="text-white/60 text-sm">Automacao</div>
              </div>
              <div>
                <div className="text-2xl font-bold">Content</div>
                <div className="text-white/60 text-sm">Hub</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-[520px] lg:min-w-[520px] flex flex-col bg-white dark:bg-gray-900 relative shadow-[-4px_0_30px_rgba(0,0,0,0.1)] dark:shadow-[-4px_0_30px_rgba(0,0,0,0.3)]">
        {/* Theme Toggle */}
        <div className="absolute top-6 right-6 z-10">
          <button
            onClick={toggleTheme}
            className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-xl"
            title={theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
          >
            {theme === 'dark' ? '\u2600\uFE0F' : '\uD83C\uDF19'}
          </button>
        </div>

        {/* Logo */}
        <div className="flex justify-center pt-24 lg:pt-20 px-12">
          <img src="/Logo-nai-growth.webp" alt="Nai Growth" className="h-20 max-w-[300px] object-contain dark:hidden" />
          <img src="/Logo-nai-growth-branco.webp" alt="Nai Growth" className="h-20 max-w-[300px] object-contain hidden dark:block" />
        </div>

        {/* Form Content */}
        <div className="flex-1 flex items-center justify-center px-12 py-8">
          <div className="w-full max-w-[400px]">

            {/* ---- FORCE PASSWORD CHANGE ---- */}
            {mustChangePassword ? (
              <>
                <div className="mb-8 text-center">
                  <div className="mx-auto w-16 h-16 rounded-full bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center mb-4">
                    <ShieldCheck className="h-8 w-8 text-amber-600 dark:text-amber-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Alterar Senha
                  </h2>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    Por seguranca, voce precisa definir uma nova senha para continuar.
                  </p>
                </div>

                {pwdError && (
                  <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm text-center">
                    {pwdError}
                  </div>
                )}

                <form onSubmit={handlePasswordChange} className="space-y-5">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Nova Senha</label>
                    <Input
                      type="password"
                      placeholder="Minimo 8 caracteres"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoFocus
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Confirmar Nova Senha</label>
                    <Input
                      type="password"
                      placeholder="Repita a nova senha"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>

                  <div className="text-xs text-gray-400 dark:text-gray-500 space-y-1">
                    <p>A senha deve conter:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li className={newPassword.length >= 8 ? 'text-green-500' : ''}>Minimo 8 caracteres</li>
                      <li className={/[A-Z]/.test(newPassword) ? 'text-green-500' : ''}>Uma letra maiuscula</li>
                      <li className={/[a-z]/.test(newPassword) ? 'text-green-500' : ''}>Uma letra minuscula</li>
                      <li className={/\d/.test(newPassword) ? 'text-green-500' : ''}>Um numero</li>
                    </ul>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11"
                    disabled={!newPassword || !confirmPassword}
                    isLoading={changePwdMutation.isPending}
                  >
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Alterar Senha
                  </Button>
                </form>
              </>
            ) : totpStep ? (
              <>
                <div className="mb-8 text-center">
                  <div className="mx-auto w-16 h-16 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-4">
                    <ShieldCheck className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Verificacao em duas etapas
                  </h2>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    Digite o codigo de 6 digitos do seu aplicativo autenticador
                  </p>
                </div>

                {error && (
                  <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm text-center">
                    {error}
                  </div>
                )}

                {/* 6-digit code input */}
                <div className="flex justify-center gap-3 mb-8" onPaste={handleTotpPaste}>
                  {totpCode.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => { inputRefs.current[i] = el }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleTotpInput(i, e.target.value)}
                      onKeyDown={(e) => handleTotpKeyDown(i, e)}
                      className="w-12 h-14 text-center text-2xl font-bold border-2 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                      disabled={isVerifying}
                    />
                  ))}
                </div>

                <Button
                  type="button"
                  className="w-full h-11 mb-4"
                  isLoading={isVerifying}
                  onClick={() => {
                    const code = totpCode.join('')
                    if (code.length === 6) verifyTotp(code)
                  }}
                  disabled={totpCode.join('').length < 6}
                >
                  <ShieldCheck className="h-4 w-4 mr-2" />
                  Verificar
                </Button>

                <button
                  type="button"
                  onClick={backToLogin}
                  className="w-full flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Voltar ao login
                </button>

                {/* === Google Authenticator download helpers === */}
                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Smartphone className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Ainda nao tem o Google Authenticator?
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <a
                      href="https://apps.apple.com/app/google-authenticator/id388497605"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center gap-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all group"
                      aria-label="Baixar Google Authenticator para iOS"
                    >
                      <img
                        src="https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=https%3A%2F%2Fapps.apple.com%2Fapp%2Fgoogle-authenticator%2Fid388497605&margin=0&format=svg&color=0F172A&bgcolor=FFFFFF"
                        alt="QR Code App Store"
                        className="w-20 h-20 rounded bg-white"
                        loading="lazy"
                      />
                      <div className="text-center">
                        <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-semibold">App Store</div>
                        <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 group-hover:text-blue-600 dark:group-hover:text-blue-400">iOS</div>
                      </div>
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center gap-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all group"
                      aria-label="Baixar Google Authenticator para Android"
                    >
                      <img
                        src="https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=https%3A%2F%2Fplay.google.com%2Fstore%2Fapps%2Fdetails%3Fid%3Dcom.google.android.apps.authenticator2&margin=0&format=svg&color=0F172A&bgcolor=FFFFFF"
                        alt="QR Code Google Play"
                        className="w-20 h-20 rounded bg-white"
                        loading="lazy"
                      />
                      <div className="text-center">
                        <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-semibold">Google Play</div>
                        <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 group-hover:text-blue-600 dark:group-hover:text-blue-400">Android</div>
                      </div>
                    </a>
                  </div>
                  <p className="text-[11px] text-center text-gray-400 dark:text-gray-500 mt-3 leading-relaxed">
                    Aponte a camera do celular para o QR ou clique no card.
                  </p>
                </div>
              </>
            ) : (
              /* ---- NORMAL LOGIN STEP ---- */
              <>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Bem-vindo de volta
                  </h2>
                  <p className="text-gray-500 dark:text-gray-400">
                    Acesse sua conta para continuar
                  </p>
                </div>

                {error && (
                  <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm flex items-center justify-between animate-in fade-in duration-300">
                    <span>{error}</span>
                    <button type="button" onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700 dark:hover:text-red-300 flex-shrink-0">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-gray-700 dark:text-gray-300">E-mail</label>
                    <Input id="email" type="email" placeholder="seu@email.com" autoComplete="email" error={errors.email?.message} {...register('email')} />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-300">Senha</label>
                    <div className="relative">
                      <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Digite sua senha" autoComplete="current-password" error={errors.password?.message} {...register('password')} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" tabIndex={-1}>
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500" />
                      <span className="text-gray-500 dark:text-gray-400">Lembrar-me</span>
                    </label>
                    <button type="button" onClick={() => { setError(null); alert('Para redefinir sua senha, entre em contato com o administrador do sistema ou envie um email para suporte: juliano@nai-it.com') }} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors font-medium">
                      Esqueceu a senha?
                    </button>
                  </div>

                  <Button type="submit" className="w-full h-11" isLoading={isLoading}>
                    <LogIn className="h-4 w-4 mr-2" />
                    Entrar
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-12 py-5 border-t border-gray-200 dark:border-gray-700 text-center bg-white dark:bg-gray-900">
          <p className="text-sm text-gray-400 dark:text-gray-500">
            &copy; {new Date().getFullYear()} NAI IT. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </div>
  )
}

